import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuthToken, setAuthToken, removeAuthToken, getStoredUser, setStoredUser, removeStoredUser, apiRequest } from '../lib/utils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored user on mount
    const storedUser = getStoredUser();
    const token = getAuthToken();
    
    if (storedUser && token) {
      setUser(storedUser);
      // Verify token is still valid
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const verifyToken = async () => {
    try {
      const userData = await apiRequest('/auth/me');
      setUser(userData);
      setStoredUser(userData);
    } catch (error) {
      // Token invalid, clear storage
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
    
    setAuthToken(response.token);
    setStoredUser(response.user);
    setUser(response.user);
    
    return response.user;
  };

  const register = async (name, email, password, phone) => {
    const response = await apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password, phone })
    });
    
    setAuthToken(response.token);
    setStoredUser(response.user);
    setUser(response.user);
    
    return response.user;
  };

  const logout = () => {
    removeAuthToken();
    removeStoredUser();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      isAuthenticated: !!user,
      isAdmin: user?.is_admin || false
    }}>
      {children}
    </AuthContext.Provider>
  );
};
